import React, {useState, useEffect, useMemo, useCallback} from 'react';
import {connect, useSelector} from 'react-redux';
import {useQuery, useQueryClient} from 'react-query';
import {
  fetchAllSearches,
  amlExport,
  fetchCompanyTitles,
} from 'requests/queries/aml';
import {
  DashboardLayout,
  EmptyState,
  Loader,
  PageLimit,
  Pagination,
  PrimaryButton,
  SearchInput,
  SecondaryButton,
  TableLayout,
} from 'components';
import {eye, corruptedFile} from 'assets/images/images';
import moment from 'moment';
import PerformSearchModal from './modals/PerformSearchModal';
import SingleSearchModal from './modals/SingleSearchModal';
import BatchAMLModal from './modals/BatchAMLModal';
import FilterV2 from 'components/FilterV2';
import PageNumber from 'components/PageNumber';
import * as Actions from 'store/actions';
import {PAGES_URL} from 'helpers/constants';
import {useNavigate} from 'react-router';
import {OverviewCards} from 'components/OverviewCards';
import {toast} from 'react-toastify';
import {dateRanges} from 'helpers/getDateRange';

const AMLScreeningNew = ({team = {}, getTeamMembers}) => {
  const navigate = useNavigate();
  const {userDetails, user} = useSelector(state => state.auth);
  const queryClient = useQueryClient();
  const [query, setQuery] = useState({page: 1, limit: 10});
  const [showFilter, setShowFilter] = useState(true);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showSingleSearchModal, setShowSingleSearchModal] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const {start, end} = dateRanges.seven_days();
  const [filterParams, setFilterParams] = useState({
    date: 'seven_days',
    date_from: start,
    date_to: end,
  });
  const [companyTitles, setCompanyTitles] = useState([]);

  const fetchTitles = useCallback(async () => {
    try {
      const params = {
        company_id: userDetails?.company?.id || '',
      };
      const response = await fetchCompanyTitles({params});
      setCompanyTitles(response?.titles || []);
    } catch (error) {
      console.error('Error fetching company titles:', error);
      toast.error('Failed to fetch company titles.');
    }
  }, [userDetails?.company?.id]);

  useEffect(() => {
    getTeamMembers('status=active');
    if (userDetails?.company?.id) {
      fetchTitles();
    }
  }, [getTeamMembers, userDetails?.company?.id, fetchTitles]);

  const assignedOptions = useMemo(() => {
    const uniqueMembers = [];
    const seenValues = new Set();

    (team?.members?.team || []).forEach(member => {
      const value =
        `${member?.user?.first_name}+${member?.user?.last_name}`.toLowerCase();
      if (!seenValues.has(value)) {
        seenValues.add(value);
        uniqueMembers.push({
          value,
          label: `${member?.user?.first_name} ${member?.user?.last_name}`,
        });
      }
    });

    return uniqueMembers;
  }, [team?.members]);

  const titleOptions = useMemo(
    () =>
      companyTitles
        .filter(titleObj => titleObj.title !== null)
        .map(titleObj => ({
          value: titleObj.title,
          label: titleObj.title,
        })),
    [companyTitles],
  );

  const filters = useMemo(
    () => [
      {
        key: 'match_status',
        label: 'Match Status',
        type: 'select',
        multiSelect: false,
        options: [
          {value: 'no match', label: 'No Match'},
          {value: 'partial match', label: 'Partial Match'},
          {value: 'potential match', label: 'Potential Match'},
          {value: 'confirmed match', label: 'Confirmed Match'},
        ],
      },
      {
        key: 'risk_level',
        label: 'Risk Level',
        type: 'select',
        multiSelect: false,
        options: [
          {value: 'low', label: 'Low'},
          {value: 'medium', label: 'Medium'},
          {value: 'high', label: 'High'},
        ],
      },
      {
        key: 'monitoring',
        label: 'Monitoring',
        type: 'select',
        multiSelect: false,
        options: [
          {value: 'one time', label: 'One Time'},
          {value: 'ongoing', label: 'Ongoing'},
        ],
      },
      {
        key: 'assigned_to',
        label: 'Assigned To',
        type: 'select',
        multiSelect: false,
        options: [...assignedOptions],
      },
      {
        key: 'title',
        label: 'Bulk Title',
        type: 'select',
        multiSelect: false,
        options: titleOptions,
      },
    ],
    [assignedOptions, titleOptions],
  );

  const handleFilterChange = values => {
    setFilterParams({
      match_status: values.match_status,
      risk_level: values.risk_level,
      monitoring: values.monitoring,
      assigned_to: values.assigned_to,
      title: values.title,
      date_from: values.dateRange?.start,
      date_to: values.dateRange?.end,
    });
    setQuery(prev => ({...prev, page: 1}));
  };

  const params = useMemo(
    () => ({
      company_id: userDetails?.company?.id || '',
      ...query,
      search: query.search_query || '',
      match_status: filterParams.match_status || '',
      risk_level: filterParams.risk_level || '',
      monitoring: filterParams.monitoring || '',
      assigned_to: filterParams.assigned_to || '',
      title: filterParams.title || '',
      date_from: filterParams.date_from,
      date_to: filterParams.date_to,
    }),
    [query, userDetails, filterParams],
  );

  const {data, isLoading, isFetching, refetch} = useQuery(
    ['all-searches', {params}],
    () => fetchAllSearches({params}),
    {
      onError: err => {
        console.error('Error fetching searches:', err);
        toast.error('Failed to load search data.');
      },
    },
  );

  const handleExportData = async () => {
    try {
      setIsExporting(true);
      const exportParams = {
        company_id: userDetails?.company?.id || '',
        format: 'csv',
        include_profiles: true,
        email: user?.email || '',
        page: query.page,
        limit: query.limit,
        search: query.search_query || '',
        match_status: filterParams.match_status || '',
        risk_level: filterParams.risk_level || '',
        monitoring: filterParams.monitoring || '',
        assigned_to: filterParams.assigned_to || '',
        title: filterParams.title || '',
        date_from: filterParams.date_from,
        date_to: filterParams.date_to,
      };

      await amlExport({params: exportParams});
      toast.success('Export will be sent to your email');
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Failed to export data. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const tableHeader = [
    {label: 'name', key: 'name'},
    {label: 'Risk level', key: 'risk_level'},
    {label: 'Result', key: 'value'},
    {label: 'Status', key: 'status'},
    {label: 'Entity', key: 'entity'},
    {label: 'Last updated', key: 'lastUpdated'},
    {label: 'Monitoring', key: 'monitoring'},
  ];

  const tableCardData = useMemo(() => {
    const analysisMap =
      data?.analysis?.reduce((acc, item) => {
        if (item?.match_status && typeof item.match_status === 'string') {
          const status = item.match_status.toLowerCase();
          acc[status] = item.count;
        }
        return acc;
      }, {}) || {};

    return [
      {
        title: 'No Match Cases',
        value: (analysisMap['no match'] || 0).toLocaleString(),
      },
      {
        title: 'Potential Matched Cases',
        value: (analysisMap['potential match'] || 0).toLocaleString(),
      },
      {
        title: 'Partially Matched Cases',
        value: (analysisMap['partial match'] || 0).toLocaleString(),
      },
      {
        title: 'Confirmed Matched Cases',
        value: (analysisMap['confirmed match'] || 0).toLocaleString(),
      },
    ];
  }, [data?.analysis]);

  const handleAction = action => {
    switch (action) {
      case 'toggleFilter':
        return setShowFilter(prev => !prev);
      case 'openSearchModal':
        return setShowSearchModal(true);
      case 'closeSearchModal':
        return setShowSearchModal(false);
      case 'openSingleSearchModal':
        setShowSearchModal(false);
        setShowSingleSearchModal(true);
        break;
      case 'openBatchModal':
        setShowSearchModal(false);
        setShowBatchModal(true);
        break;
      case 'closeSingleSearchModal':
        return setShowSingleSearchModal(false);
      case 'closeBatchModal':
        return setShowBatchModal(false);
      case 'switchToOldVersion':
        navigate(PAGES_URL.AML_USER_SCREENING);
        break;
      case 'searchSuccess':
        return () => {
          setShowSearchModal(false);
          setShowSingleSearchModal(false);
        };
      default:
        return null;
    }
  };

  return (
    <DashboardLayout
      breadCrumbs={
        <p className="text-tiny font-normal text-deepBlue">AML Screening</p>
      }
    >
      <div className="flex items-center py-2 flex-wrap gap-3 justify-between mx-[-32px] px-8 border border-transparent border-b-[#E4E3EC]">
        <div className="flex items-center gap-2 flex-wrap">
          <SearchInput
            placeholder="Search name"
            onChange={value =>
              setQuery(prev => ({...prev, search_query: value, page: 1}))
            }
            defaultValue={query.search_query || ''}
          />
          <SecondaryButton
            buttonText="Filter"
            onClick={() => handleAction('toggleFilter')}
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <SecondaryButton
            buttonText={isExporting ? 'Exporting...' : 'Export Data'}
            onClick={handleExportData}
            disabled={isExporting}
            loading={isExporting}
          />
          <SecondaryButton
            buttonText="Switch to Old Version"
            onClick={() => handleAction('switchToOldVersion')}
          />
          <PrimaryButton
            buttonText="Perform Search"
            onClick={() => handleAction('openSearchModal')}
          />
        </div>
      </div>

      <div className={`filter-slide-container ${showFilter ? 'open' : ''}`}>
        <FilterV2
          filters={filters}
          onFilterChange={handleFilterChange}
          initialValues={filterParams}
        />
      </div>

      <PerformSearchModal
        show={showSearchModal}
        onClose={() => handleAction('closeSearchModal')}
        onSingleSearchClick={() => handleAction('openSingleSearchModal')}
        onBatchLookup={() => handleAction('openBatchModal')}
      />
      <BatchAMLModal
        show={showBatchModal}
        onClose={() => handleAction('closeBatchModal')}
        onSuccessRefetch={() => {
          refetch();
          fetchTitles();
        }}
        companyTitles={companyTitles}
        user={user}
      />
      <SingleSearchModal
        show={showSingleSearchModal}
        onClose={() => handleAction('closeSingleSearchModal')}
        queryClient={queryClient}
        onSearchSuccess={() => handleAction('searchSuccess')}
        user={user}
      />

      <OverviewCards
        cardData={tableCardData}
        overviewLoading={isLoading || isFetching}
        className="grid md:grid-cols-2 lg:grid-cols-4 gap-2 lg:gap-4 flex-wrap py-6"
      />

      <TableLayout negativeMargins>
        <thead className="font-semibold uppercase">
          <tr className="bg-white80">
            {tableHeader.map(({label, key}) => (
              <th
                key={key}
                className={`${
                  label === 'name' ? 'pl-4 sm:pl-6 xl:pl-8' : 'pl-0'
                } h-11 font-medium text-left text-small text-[#5C688E] whitespace-nowrap`}
              >
                <div className="flex items-end gap-1 cursor-pointer">
                  <span>{label}</span>
                </div>
              </th>
            ))}
            <th></th>
          </tr>
        </thead>
        <tbody className="text-sm bg-white">
          {isLoading || isFetching ? (
            <tr>
              <td colSpan={tableHeader.length + 1} className="p-4 text-center">
                <Loader height={50} />
              </td>
            </tr>
          ) : (data?.search || []).length > 0 ? (
            (data.search || []).map(item => (
              <tr
                onClick={() =>
                  navigate(`/aml-screening/v2/results/${item.id}`, {
                    state: {customerName: item.search_query || 'Unknown'},
                  })
                }
                key={item.id}
                className="border-[0.5px] border-[#E4E3EC] cursor-pointer"
              >
                <td className="p-4 pl-4 sm:pl-6 xl:pl-8 text-body whitespace-nowrap capitalize">
                  {item.search_query || '-'}
                </td>
                <td className="p-4 pl-0 text-body whitespace-nowrap">
                  <div
                    className={`px-2 py-1 text-small font-medium rounded-full w-fit ${
                      item?.risk_level && typeof item.risk_level === 'string'
                        ? item.risk_level.toLowerCase() === 'low'
                          ? 'bg-[#F6F9F5] text-[#55A249]'
                          : item.risk_level.toLowerCase() === 'medium'
                          ? 'bg-[#FFFAF0] text-[#DC6803]'
                          : item.risk_level.toLowerCase() === 'high'
                          ? 'bg-[#FBF4F4] text-[#B42318]'
                          : 'bg-gray-200 text-gray-700'
                        : 'bg-gray-200 text-gray-700'
                    }`}
                  >
                    {item?.risk_level && typeof item.risk_level === 'string'
                      ? item.risk_level.toLowerCase() === 'unknown'
                        ? 'Unknown'
                        : `${
                            item.risk_level.charAt(0).toUpperCase() +
                            item.risk_level.slice(1).toLowerCase()
                          } level`
                      : 'Unknown'}
                  </div>
                </td>
                <td className="p-4 pl-0 text-body whitespace-nowrap">
                  {item?.total_results || '-'}
                </td>
                <td className="p-4 pl-0 text-body whitespace-nowrap">
                  <div
                    className={`px-2 py-1 text-small font-medium rounded-full w-fit ${
                      item?.match_status &&
                      typeof item.match_status === 'string'
                        ? item.match_status.toLowerCase() === 'confirmed match'
                          ? 'bg-[#FBF4F4] text-[#B42318]'
                          : item.match_status.toLowerCase() === 'partial match'
                          ? 'bg-[#F2F6FD] text-[#3F7CDB]'
                          : item.match_status.toLowerCase() ===
                            'potential match'
                          ? 'bg-[#F0FFFE] text-[#17A6A0]'
                          : item.match_status.toLowerCase() === 'no match'
                          ? 'bg-[#F7F7F7] text-black'
                          : 'bg-disabled'
                        : 'bg-disabled'
                    }`}
                  >
                    {item?.match_status && typeof item.match_status === 'string'
                      ? (() => {
                          const statusMap = {
                            'partial match': 'partially matched',
                            'confirmed match': 'confirmed match',
                            'potential match': 'potential match',
                            'no match': 'no match',
                          };
                          const displayStatus =
                            statusMap[item.match_status.toLowerCase()] ||
                            item.match_status.toLowerCase();
                          return displayStatus
                            .split(' ')
                            .map((word, index) =>
                              index === 0
                                ? word.charAt(0).toUpperCase() + word.slice(1)
                                : word,
                            )
                            .join(' ');
                        })()
                      : 'Unknown'}
                  </div>
                </td>
                <td className="p-4 pl-0 text-body whitespace-nowrap">
                  <span
                    className={`py-1 px-2 rounded-full font-medium text-white text-small ${
                      item?.schema_type === 'individual'
                        ? 'bg-[#0B1C56]'
                        : 'bg-[#17A6A0]'
                    }`}
                  >
                    {item?.schema_type === 'individual'
                      ? 'Individual'
                      : 'Organization'}
                  </span>
                </td>
                <td className="p-4 pl-0 whitespace-nowrap">
                  {item?.createdAt
                    ? moment(item.createdAt).format('Do MMM, YYYY h:mm A')
                    : '-'}
                </td>
                <td className="p-4 pl-0 text-body whitespace-nowrap">
                  <span
                    className={`py-1 px-2 rounded-full font-medium text-small ${
                      item?.monitoring
                        ? 'bg-[#F2F6FD] text-[#3F7CDB]'
                        : 'bg-[#F0FFFE] text-[#17A6A0]'
                    }`}
                  >
                    {item?.monitoring ? 'Ongoing' : 'One time'}
                  </span>
                </td>
                <td className="p-4 pl-0 whitespace-nowrap">
                  <button title="View" className="h-4 aspect-square">
                    <img src={eye} alt="View" />
                  </button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={tableHeader.length + 1} className="p-4 text-center">
                <EmptyState
                  height={35}
                  body={'Perform AML Searches with ease'}
                  src={corruptedFile}
                  noBtn
                />
              </td>
            </tr>
          )}
        </tbody>
      </TableLayout>

      {(data?.search || []).length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-4 sm:gap-0 bg-white80 mx-[-32px] px-8 py-[18px] border-[0.5px] border-y-[#E4E3EC] border-transparent">
          <PageNumber
            currentPage={query.page}
            limit={query.length}
            totalCount={data?.total || 0}
          />
          <div className="flex items-center gap-5">
            <PageLimit
              onLimitChange={limit =>
                setQuery(prev => ({...prev, limit, page: 1}))
              }
              total={data?.total || 0}
              limit={query.limit}
              currentCount={(data?.search || []).length}
            />
            <Pagination
              total={data?.total ? Math.ceil(data.total / query.limit) : 1}
              current={query.page}
              onPageChange={page => setQuery(prev => ({...prev, page}))}
            />
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default connect(state => ({team: state.team}), {
  getTeamMembers: Actions.getTeamMembers,
})(AMLScreeningNew);
